/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState } from "react";

import classNames from "classnames";
import posthog from "posthog-js";

import { YellowBannerObject } from "data/homepage";
import { ProductData } from "data/product/products";
import { StorefrontData } from "data/storefront";
import { Include } from "data/videoProductPage";
import { getIncludedSectionData } from "data/videoProductPage";
import { Button } from "studio-design-system";
import { useWindowSize } from "usehooks-ts";

import { PRODUCT_PAGE_BUY_NOW_SELECTED } from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { PlayVideoIcon } from "~/components/common/Icons/PlayVideoIcon";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { LandingPageSources, useProductLandingPage } from "~/components/homepage/useLandingPage";
import * as productStyles from "~/components/videoProductPage/Product.css";
import { ClassVideoModal } from "~/components/videoProductPage/common/ClassVideoModal/ClassVideoModal";
import { Divider } from "~/components/videoProductPage/common/Divider/Divider";
import { VideoPlayer } from "~/components/videoProductPage/common/VideoPlayer/VideoPlayer";
import usePriceCountdown from "~/components/videoProductPage/common/usePriceCountdown";
import { VideoChaptersSection } from "~/components/videoProductPage/components/VideoSection/VideoChaptersSection/VideoChaptersSection";
import { checkLineNumber } from "~/components/videoProductPage/utils/checkLineNumber";
import { useAuth } from "~/hooks/auth/useAuth";
import { useIsMobileScreen } from "~/hooks/ui/useIsMobileScreen";
import { useCreatorEditing, CreatorSavingType } from "~/hooks/useCreatorEditing";
import { usePriceOfferInfoCheckout } from "~/hooks/usePriceOfferInfo";
import { getResponsiveImageURL } from "~/util/cloudinary-utils";
import { addLinksToText, formatMoney } from "~/util/stringUtils";

import { AddToCartButton } from "../AddToCartButton/AddToCartButton";
import { ApplyKnowledge } from "./ApplyKnowledge";
import { PreviewForm } from "./PreviewForm";
import { TaughtBySection } from "./TaughtBySection/TaughtBySection";
import * as styles from "./VideoSection.css";
import { WhatYouWillLearn } from "./WhatYouWillLearn/WhatYouWillLearn";

const PREVIEW_LESSON_STOREFRONT_SLUGS_TO_NOT_HIDE = ["casey-neistat", "kevinparry"];
export const VideoSection = ({
  product,
  storefrontData,
  handleAddToCartShowModal,
  isInEditMode,
}: {
  product: ProductData;
  storefrontData: StorefrontData;
  handleAddToCartShowModal: () => void;
  isInEditMode: boolean;
}): React.ReactElement => {
  const isMobileScreen = useIsMobileScreen();
  const priceInfo = product.priceInfo;
  const { expired, days, hours, minutes, seconds } = usePriceCountdown({
    targetDate: priceInfo.crossedPriceApplicableFrom,
  });
  const DESKTOP_CHARACTER_COUNT_LIMIT = 1160;
  const { trackAmplitudeEvent } = useAnalytics();
  const { handleTextChange, handleTextEdit, fieldsTexts, fieldsOnError, isSavingChanges } =
    useCreatorEditing();
  const [showMore, setShowMore] = useState(false);
  const [showMoreClassDescription, setShowMoreClassDescription] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [lineNumber, setLineNumber] = useState(0);
  const textBlockRef = useRef<HTMLParagraphElement>(null);
  const descriptionBlockRef = useRef<HTMLDivElement>(null);
  const [truncatedText, setTruncatedText] = useState({
    content: product.description,
    truncated: false,
  });
  const [truncatedClassText, setTruncatedClassText] = useState({
    content: product.description,
    truncated: false,
  });
  const { shouldUserSeeOfferCalculation } = usePriceOfferInfoCheckout();
  const [showPriceOffer, setShowPriceOffer] = useState(false);
  const [offerTheme, setOfferTheme] = useState("none");
  const { user, loadingCurrentUser } = useAuth();
  const { width } = useWindowSize();

  const [isBuyNowPrioritized, setIsBuyNowPrioritized] = useState(false);
  const [isLessonPreviewExperimentShown, setIsLessonPreviewExperimentShown] = useState(false);

  const hasVideoURL = !!product.productPreviewURL;

  const { onCtaClick } = useProductLandingPage(LandingPageSources.Product, product);

  const handleTextExpansionClick = () => {
    if (truncatedText.truncated) {
      setShowMore((prev) => {
        if (!prev) scrollToDescription();
        return !prev;
      });
    }
  };

  const handleShowModal = () => setShowModal(!showModal);

  useEffect(() => {

  }, [fieldsTexts, isSavingChanges]);
  useEffect(() => {

    setLineNumber(checkLineNumber(textBlockRef.current, product.description));
  }, [width]);

  useEffect(() => {

    const shouldUserSeeOffer = shouldUserSeeOfferCalculation(priceInfo);


    setShowPriceOffer(shouldUserSeeOffer);
    // if (isInEditMode) {
    //   setShowPriceOffer(true);
    // }

    // posthog.feature_flags.override({ "flip-purchase-button": "buy-now-priority" });
    if (posthog.getFeatureFlag("flip-purchase-button") === "buy-now-priority") {
      // Do something differently for this user
      setIsBuyNowPrioritized(true);

    } else {
      // It's a good idea to let control variant always be the default behaviour,
      // so if something goes wrong with flag evaluation, you don't break your app.
      setIsBuyNowPrioritized(false);
    }

    posthog.onFeatureFlags(function () {
      if (posthog.isFeatureEnabled("homepage-yellow-banner-text")) {
        const yellowBannerInfo = posthog.getFeatureFlagPayload(
          "homepage-yellow-banner-text",
        ) as unknown as YellowBannerObject;
        if (yellowBannerInfo) {
          setOfferTheme(yellowBannerInfo.specialTheme);
        }
      }
    });

    if (posthog.getFeatureFlag("lesson-preview") === "test") {
      setIsLessonPreviewExperimentShown(true);
    } else {
      setIsLessonPreviewExperimentShown(false);
    }
  }, [user, loadingCurrentUser]);

  const showMoreClassDescriptionHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMoreClassDescription(false);
  };

  const showLessClassDescriptionHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMoreClassDescription(true);
  };

  useEffect(() => {
    if (product.overviewSectionDescription.length <= DESKTOP_CHARACTER_COUNT_LIMIT) {
      setTruncatedClassText({ content: product.overviewSectionDescription, truncated: false });
      setShowMoreClassDescription(false);
      return;
    }

    setShowMoreClassDescription(true);
    const truncatedText =
      product.overviewSectionDescription.slice(0, DESKTOP_CHARACTER_COUNT_LIMIT) + "...";
    setTruncatedClassText({
      content: truncatedText,
      truncated: true,
    });
  }, [product.overviewSectionDescription]);

  const showMoreHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMore(false);
  };

  const showLessHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMore(true);
    scrollToDescription();
  };

  const scrollToDescription = () => {
    if (descriptionBlockRef.current) {
      const descriptionTop = descriptionBlockRef.current.offsetTop;
      const averageDescriptionHeight = 200;
      const visibleHeight = window.innerHeight;
      const scrollPosition = descriptionTop - (visibleHeight - averageDescriptionHeight) / 1.4;
      window.scrollTo({ top: scrollPosition });
    }
  };

  const handleBuyNow = () => {
    trackAmplitudeEvent(PRODUCT_PAGE_BUY_NOW_SELECTED, {
      ...addUTMPropertiesToEvent(),
    });
    onCtaClick();
  };

  const errorBorderStyle = { outline: "2px solid red" };

  return (
    <div className={styles.productInfo}>
      <div className={styles.aboutPageContainer}>
        <div className={styles.aboutPage}>
          {/* <TvIcon /> */}
          <p className={styles.aboutPageTitle}>Online Studio Class</p>
        </div>
      </div>
      <div onClick={handleShowModal} className={styles.productBuyVideo}>
        <div className={styles.videoPlayerOverlay}></div>
        <VideoPlayer
          url="https://"
          light={getResponsiveImageURL(product.productImageURL, "500")}
          playIcon={
            <div className={styles.previewImageContainer}>
              {hasVideoURL && (
                <>
                  <PlayVideoIcon height={45} width={45} />
                  <p className={styles.previewImageText}>Watch Trailer</p>
                </>
              )}
            </div>
          }
        />
      </div>
      <ClassVideoModal
        videoUrl={product.productPreviewURL}
        isTrailerVideoOpen={showModal}
        toggleVideoModal={() => setShowModal(!showModal)}
      />
      <div className={styles.titleBlock}>
        <h1
          className={classNames(styles.title, isInEditMode && productStyles.editableElement)}
          contentEditable={isInEditMode}
          suppressContentEditableWarning={true}
          onBlur={(e) => handleTextChange(e, "title", CreatorSavingType.PRODUCT)}
          onInput={(e) => handleTextEdit(e, "title", CreatorSavingType.PRODUCT)}
          style={fieldsOnError.includes("title") ? errorBorderStyle : undefined}
        >
          {isInEditMode ? fieldsTexts["title"] : product.title}
        </h1>
      </div>
      <div
        ref={descriptionBlockRef}
        onClick={handleTextExpansionClick}
        className={classNames(
          styles.productContainer,
          styles.descriptionBlock,
          truncatedText.truncated && styles.pointer,
        )}
      >
        <div className={styles.description}>
          <div
            className={classNames(isInEditMode && productStyles.editableElement)}
            contentEditable={isInEditMode}
            suppressContentEditableWarning={true}
            onBlur={(e) => handleTextChange(e, "description", CreatorSavingType.PRODUCT)}
            onInput={(e) => handleTextEdit(e, "description", CreatorSavingType.PRODUCT)}
            style={fieldsOnError.includes("description") ? errorBorderStyle : undefined}
          >
            {isInEditMode ? fieldsTexts["description"] : product.description}
          </div>

          {showMore && truncatedText.truncated && (
            <span dangerouslySetInnerHTML={{ __html: addLinksToText(truncatedText.content) }} />
          )}
          {/* {showMore && truncatedText.truncated && addLinksToText(truncatedText.content)} */}

          {showMore && (
            <span onClick={showMoreHandler} className={classNames(styles.videoSectionShowMore)}>
              More
            </span>
          )}
        </div>

        {!showMore && truncatedText.truncated && (
          <span onClick={showLessHandler} className={styles.videoSectionShowLess}>
            Less
          </span>
        )}
      </div>
      <div className={classNames(styles.productContainer, styles.productBuyInfo)}>
        <div className={styles.productBuyPrice}>
          <span className={styles.productBuyNewPrice}>
            {formatMoney(showPriceOffer || isInEditMode ? priceInfo.price : priceInfo.crossedPrice)}
          </span>
          {showPriceOffer || isInEditMode ? (
            <span className={styles.productBuyOldPrice}>{formatMoney(priceInfo.crossedPrice)}</span>
          ) : (
            <></>
          )}
        </div>
        <>
          {isBuyNowPrioritized ? (
            <>
              <Button
                onClick={handleBuyNow}
                disabled={isInEditMode}
                className={classNames(styles.ctaButton)}
              >
                Buy now
              </Button>
              <AddToCartButton
                variant={"secondary"}
                product={product}
                disabled={isInEditMode}
                className={classNames(styles.ctaButton)}
                handleAddToCartShowModal={handleAddToCartShowModal}
              />
            </>
          ) : (
            <>
              <AddToCartButton
                product={product}
                disabled={isInEditMode}
                handleAddToCartShowModal={handleAddToCartShowModal}
              />
              <Button
                variant={"secondary"}
                onClick={handleBuyNow}
                disabled={isInEditMode}
                className={classNames(styles.ctaButton, styles.black)}
              >
                Buy now
              </Button>
            </>
          )}
        </>
        {showPriceOffer && (
          <div className={styles.offerInfo}>
            <div
              className={classNames(
                styles.offerBlock,
                offerTheme !== "none" && styles.springTheme,
                offerTheme === "holiday" && styles.holidayTheme,
                offerTheme === "spring" && styles.springTheme,
              )}
            >
              <div className={styles.offerInfoText}>
                {offerTheme === "spring" ? "Spring Offer " : "Offer"} ends in
              </div>
              <div className={styles.counter}>
                {days ? ` ${days}d` : "0d"}
                {hours ? ` ${hours}h` : " 0h"}
                {minutes ? ` ${minutes}m` : " 0m"}
                {seconds ? ` ${seconds}s` : " 0s"}
              </div>
            </div>
          </div>
        )}
        <Divider marginTop={11} marginBottom={29} />
        <div>
          <p className={styles.includedTitle}>What’s included:</p>
          <ul>
            {getIncludedSectionData(product).map((includeItem: Include) => (
              <li key={includeItem.text} className={styles.includedItem}>
                <div className={styles.includedImageContainer}>
                  <ImageWithBlur
                    className={styles.includedItemImage}
                    src={includeItem.iconUrl}
                    alt="include icon"
                    layout={"fill"}
                  />
                </div>
                <p className={styles.includedItemText}>{includeItem.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <TaughtBySection
        product={product}
        storefrontData={storefrontData}
        isInEditMode={isInEditMode}
      />

      <div className={classNames(styles.productContainer)}>
        <Divider marginTop={53} marginBottom={49} />
      </div>

      <div className={classNames(styles.productContainer)}>
        <WhatYouWillLearn productData={product} isInEditMode={isInEditMode} iconColor="#000" />
      </div>

      <div className={styles.productContainer}>
        <Divider marginTop={33} marginBottom={49} />
      </div>
      <div className={classNames(styles.productContainer, styles.sectionMargin)}>
        {product.overviewSectionTitle && <p className={styles.topicsTitle}>Class Overview</p>}
        <section className={styles.sectionWrap}>
          <div
            className={classNames(
              styles.sectionTitle,
              isInEditMode && productStyles.editableElement,
            )}
            contentEditable={isInEditMode}
            suppressContentEditableWarning={true}
            onBlur={(e) => handleTextChange(e, "overviewSectionTitle", CreatorSavingType.PRODUCT)}
            onInput={(e) => handleTextEdit(e, "overviewSectionTitle", CreatorSavingType.PRODUCT)}
            style={fieldsOnError.includes("overviewSectionTitle") ? errorBorderStyle : undefined}
          >
            {isInEditMode ? fieldsTexts["overviewSectionTitle"] : product.overviewSectionTitle}
          </div>
          <div
            className={classNames(
              styles.sectionBody,
              isInEditMode && productStyles.editableElement,
            )}
            contentEditable={isInEditMode}
            suppressContentEditableWarning={true}
            onBlur={(e) =>
              handleTextChange(e, "overviewSectionDescription", CreatorSavingType.PRODUCT)
            }
            onInput={(e) =>
              handleTextEdit(e, "overviewSectionDescription", CreatorSavingType.PRODUCT)
            }
            style={
              fieldsOnError.includes("overviewSectionDescription") ? errorBorderStyle : undefined
            }
          >
            {!(showMoreClassDescription && !isInEditMode && truncatedClassText.truncated) && (
              <span>
                {addLinksToText(
                  isInEditMode
                    ? fieldsTexts["overviewSectionDescription"]
                    : product.overviewSectionDescription,
                ).trim()}
              </span>
            )}

            {showMoreClassDescription && !isInEditMode && truncatedClassText.truncated && (
              <span
                onClick={showMoreClassDescriptionHandler}
                style={{
                  cursor: "pointer",
                }}
              >
                {addLinksToText(truncatedClassText.content.trim())}
              </span>
            )}
            {showMoreClassDescription && !isInEditMode && truncatedClassText.truncated && (
              <span
                onClick={showMoreClassDescriptionHandler}
                style={{
                  cursor: "pointer",
                  marginLeft: "5px",
                  borderBottom: "1px solid #000",
                  lineHeight: "16px",
                }}
              >
                More
              </span>
            )}
            {!showMoreClassDescription && truncatedClassText.truncated && (
              <span
                onClick={showLessClassDescriptionHandler}
                style={{
                  cursor: "pointer",
                  marginLeft: "5px",
                  borderBottom: "1px solid #000",
                  lineHeight: "16px",
                }}
              >
                Less
              </span>
            )}
          </div>
        </section>
      </div>
      {/* <div className={classNames(styles.productContainer, styles.topics)}>
        <p className={styles.topicsTitle}>Topics ({product.tags && product.tags.length})</p>
        <TopicsSection topics={product.tags} />
      </div> */}

      {product.content && product.content.length > 0 && (
        <div className={classNames(styles.productContainer, styles.sectionMargin)}>
          <p className={styles.topicsTitle}>
            {product.content && product.content.length > 1 ? "Lessons " : "Lesson "}(
            {product.content && product.content.length})
          </p>
          <VideoChaptersSection productData={product} videos={product.content} />
        </div>
      )}

      {/* Show the preview form to capture email in an AB test */}
      {!isInEditMode &&
        product?.freeLessonDescription &&
        product?.previewClipURL &&
        isLessonPreviewExperimentShown && (
          <div className={classNames(styles.productContainer, styles.previewSection)}>
            <PreviewForm productData={product} />
          </div>
        )}

      {product.exampleMediaSectionTitle && product.exampleMediaSectionDescription && (
        <>
          <div className={classNames(styles.productContainer, styles.betweenTopicDivider)}>
            <Divider marginTop={58} marginBottom={49} />
          </div>

          <div className={classNames(styles.productContainer, styles.previewSection)}>
            <div>
              <p className={styles.topicsTitle}>{"Apply what you learn"}</p>
              <section className={styles.sectionWrap}>
                <div
                  className={classNames(
                    styles.sectionTitle,
                    isInEditMode && productStyles.editableElement,
                  )}
                  contentEditable={isInEditMode}
                  suppressContentEditableWarning={true}
                  onBlur={(e) =>
                    handleTextChange(e, "exampleMediaSectionTitle", CreatorSavingType.PRODUCT)
                  }
                  onInput={(e) =>
                    handleTextEdit(e, "exampleMediaSectionTitle", CreatorSavingType.PRODUCT)
                  }
                  style={
                    fieldsOnError.includes("exampleMediaSectionTitle")
                      ? errorBorderStyle
                      : undefined
                  }
                >
                  {isInEditMode
                    ? fieldsTexts["exampleMediaSectionTitle"]
                    : product.exampleMediaSectionTitle}
                </div>
                <p
                  className={classNames(
                    styles.sectionBody,
                    isInEditMode && productStyles.editableElement,
                  )}
                  contentEditable={isInEditMode}
                  suppressContentEditableWarning={true}
                  onBlur={(e) =>
                    handleTextChange(e, "exampleMediaSectionDescription", CreatorSavingType.PRODUCT)
                  }
                  onInput={(e) =>
                    handleTextEdit(e, "exampleMediaSectionDescription", CreatorSavingType.PRODUCT)
                  }
                  style={
                    fieldsOnError.includes("exampleMediaSectionDescription")
                      ? errorBorderStyle
                      : undefined
                  }
                >
                  {isInEditMode
                    ? fieldsTexts["exampleMediaSectionDescription"]
                    : product.exampleMediaSectionDescription}
                </p>
                <ApplyKnowledge
                  productData={product}
                  storefrontData={storefrontData}
                  isInEditMode={isInEditMode}
                />
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
